import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarCheck, BellRing, ListChecks, ClipboardList, FileText } from "lucide-react";
import { Reveal, Eyebrow } from "./motion";
import { PILLARS, CITIES, AUDIENCES } from "../lib/site";

export const Audiences = () => (
  <section className="bg-porcelain py-28 md:py-40" data-testid="audiences-section">
    <div className="mx-auto grid max-w-[1400px] gap-16 px-5 md:px-10 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal><Eyebrow>Who we serve</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 font-display text-4xl font-semibold leading-[1] tracking-tight text-ink md:text-6xl">Built for businesses, not homes.</h2>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-8 max-w-md text-base leading-relaxed text-slate-600 md:text-lg">
            Shine Solutions provides professionally managed commercial cleaning for businesses that want a dependable, long-term partner — with consistent scheduling, clear service standards and responsive communication.
          </p>
        </Reveal>
      </div>
      <ul className="lg:col-span-7">
        {AUDIENCES.map((a, i) => (
          <Reveal as="li" key={a.t} delay={0.05 * i} className="group grid grid-cols-[3rem_1fr] items-baseline gap-4 border-b border-slate-300 py-6 md:grid-cols-[4rem_1fr_1fr] md:py-8">
            <span className="font-mono text-xs text-emerald-700">{String(i + 1).padStart(2, "0")}</span>
            <span className="font-display text-2xl font-semibold text-ink transition-transform duration-500 group-hover:translate-x-2 md:text-3xl">{a.t}</span>
            <span className="col-start-2 text-sm text-slate-600 md:col-start-3 md:text-base">{a.d}</span>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

export const Pillars = () => (
  <section className="bg-white py-28 md:py-40" data-testid="pillars-section">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <Reveal><Eyebrow>The Shine standard</Eyebrow></Reveal>
      <Reveal delay={0.08}>
        <p className="mt-8 max-w-5xl font-display text-3xl font-medium leading-[1.15] tracking-tight text-ink md:text-5xl">
          We don't sell a clean. We <span className="font-serif italic text-emerald-700">run</span> one — the same scope, the same standard and the same point of contact on every scheduled visit.
        </p>
      </Reveal>
      <div className="mt-20 grid grid-cols-1 border-l border-t border-slate-200 sm:grid-cols-2 lg:grid-cols-3">
        {PILLARS.map((p, i) => (
          <Reveal key={p.t} delay={0.05 * i} className="pillar group relative border-b border-r border-slate-200 p-8 md:p-10">
            <span className="font-mono text-xs text-slate-500">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-10 font-display text-2xl font-semibold text-ink">{p.t}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 md:text-base">{p.d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const DRIVERS = ["Square footage and layout", "Visit frequency and service window", "Restroom and breakroom count", "Floor types and special surfaces", "After-hours access and security", "Specialty areas and periodic tasks"];

export const WhyProposal = () => (
  <section className="relative overflow-hidden bg-white py-28 md:py-40" data-testid="why-proposal-section">
    <div className="mx-auto grid max-w-[1400px] gap-16 px-5 md:px-10 lg:grid-cols-12">
      <div className="relative lg:col-span-6">
        <Reveal>
          <div className="clip-cut-alt relative h-[420px] overflow-hidden md:h-[560px]">
            <img src="/images/desk.jpg" alt="Clean modern office workspace with organized desk" loading="lazy" className="h-full w-full object-cover" />
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-6 lg:pt-10">
        <Reveal><Eyebrow>No instant pricing — on purpose</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 font-display text-4xl font-semibold leading-[1] tracking-tight text-ink md:text-6xl">Priced to your building, not a calculator.</h2>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-8 max-w-lg text-base leading-relaxed text-slate-600 md:text-lg">
            An online price for a commercial contract is a guess. We review what drives the work, then put it in writing — so the scope you approve is the scope we deliver.
          </p>
        </Reveal>
        <ul className="mt-10 divide-y divide-slate-200 border-y border-slate-200">
          {DRIVERS.map((d, i) => (
            <Reveal as="li" key={d} delay={0.04 * i} className="flex items-center justify-between py-4 text-base text-ink">
              {d}<span className="font-mono text-xs text-slate-500">{String(i + 1).padStart(2, "0")}</span>
            </Reveal>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

export const ServiceArea = () => (
  <section className="relative overflow-hidden bg-porcelain py-28 md:py-40" data-testid="service-area-section">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <div className="grid gap-10 md:grid-cols-12">
        <div className="md:col-span-4">
          <Reveal><Eyebrow>Service area</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <p className="mt-6 text-base leading-relaxed text-slate-600 md:text-lg">
              We focus on the Phoenix West Valley so crews stay close, schedules stay tight and your point of contact can be on site when it matters.
            </p>
          </Reveal>
        </div>
        <ul className="md:col-span-8">
          {CITIES.map((c, i) => (
            <Reveal as="li" key={c} delay={0.06 * i}>
              <Link to="/request-quote" data-testid={`area-${c.toLowerCase()}`} className="city-row group flex items-center justify-between border-b border-slate-300 py-5 md:py-7">
                <span className="font-display text-5xl font-semibold tracking-tight text-ink transition-transform duration-500 group-hover:translate-x-3 md:text-8xl">{c}</span>
                <span className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">AZ <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45 group-hover:text-emerald-700" /></span>
              </Link>
            </Reveal>
          ))}
          <Reveal as="li" delay={0.2}>
            <p className="py-6 font-serif text-2xl italic text-slate-600 md:text-3xl">+ nearby West Valley communities, on request</p>
          </Reveal>
        </ul>
      </div>
    </div>
  </section>
);

const OPS = [
  { i: ClipboardList, t: "Quote intake", d: "Your request lands directly in our client system." },
  { i: CalendarCheck, t: "Recurring schedule", d: "Visits booked on a fixed cadence and window." },
  { i: BellRing, t: "Reminders", d: "Confirmations and reminders before each service." },
  { i: ListChecks, t: "Visit checklists", d: "Every visit runs against your written scope." },
  { i: FileText, t: "One record", d: "Scope, schedule and history kept in one place." },
];

export const Operations = () => (
  <section className="relative bg-ink py-28 text-white md:py-40" data-testid="operations-section">
    <div className="grain" aria-hidden="true" />
    <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
      <div className="grid gap-10 md:grid-cols-12 md:items-end">
        <div className="md:col-span-7">
          <Reveal><Eyebrow dark>How accounts are run</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-display text-4xl font-semibold leading-[1] tracking-tight md:text-6xl">Systems behind the shine.</h2>
          </Reveal>
        </div>
        <Reveal delay={0.12} className="md:col-span-5">
          <p className="text-base leading-relaxed text-slate-300 md:text-lg">Once configured, client accounts will run on BookingKoala, our scheduling and service platform — so nothing depends on memory or a sticky note.</p>
        </Reveal>
      </div>
      <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-[28px] border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-5">
        {OPS.map(({ i: Icon, t, d }, k) => (
          <Reveal key={t} delay={0.05 * k} className="group bg-ink p-8 transition-colors duration-500 hover:bg-[#0E2038]">
            <Icon className="h-6 w-6 text-glint transition-transform duration-500 group-hover:-translate-y-1" />
            <h3 className="mt-10 font-display text-xl font-medium">{t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
